import Layout from "@/components/wrappers/layouts/main-layout/Layout";
import {
    IChannel,
    IMediaPlanRowObj,
    IValueBoolean,
    LayoutProps,
    TMediaPlanConfig,
    TMediaPlanRowData,
} from "@/ts/common-types";
import { Button, Divider, Input, Modal, Popover } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { FaDownload } from "react-icons/fa";
import { BsPencilFill } from "react-icons/bs";
import { useEffect, useRef, useState } from "react";
import {
    setGenerateMediaPlanData,
    setMediaPlanBudget,
    setShowMediaPlanChangeLogs,
} from "@/features/gridDataSlice";
import {
    adjustOverallMediaPlanData,
    compareMediaPlanData,
    createMediaPlan,
    editMediaPlan,
    getMappedPlanData,
    internationalizeNumericValue,
    listMediaPlans,
    resetMediaPlanningConfig,
    validatePlanData,
} from "@/ts/utils";
import { showToast } from "@/ts/modals";
import { GridApi } from "ag-grid-community";
import { isEqual, pick } from "underscore";
import {
    MEDIA_PLAN_METRICES,
    GENERATE_MEDIA_PLAN_CONFIG,
    MONTH_OPTIONS,
} from "@/ts/constants";
import MediaPlanChangeLogs from "./MediaPlanChangeLogs";

const MediaPlanLayout: React.FC<LayoutProps> = ({ children }) => {
    const dispatch = useDispatch();

    const [savePlanLoading, setSavePlanLoading] = useState(false);
    const [originalMediaPlanData, setOriginalMediaPlanData] = useState(null);

    const {
        plan_id: planId,
        plan_month: planMonth,
        plan_name: planName,
        plan_version: planVersion,
    } = useSelector(
        (state: { mediaPlanConfigValues: { value: TMediaPlanConfig } }) =>
            state.mediaPlanConfigValues.value
    );

    const isMediaPlanEdit = useSelector(
        (state: { isMediaPlanEdit: IValueBoolean }) =>
            state.isMediaPlanEdit.value
    );

    const isMediaPlanCreate = useSelector(
        (state: { isMediaPlanCreate: IValueBoolean }) =>
            state.isMediaPlanCreate.value
    );

    const mediaPlanGridApiMap = useSelector(
        (state: {
            mediaPlanGridApiMap: {
                value: Record<string, GridApi>;
            };
        }) => state.mediaPlanGridApiMap.value
    );

    const showMediaPlanChangeLogs = useSelector(
        (state: { showMediaPlanChangeLogs: IValueBoolean }) =>
            state.showMediaPlanChangeLogs.value
    );

    const currencySymbol = useSelector(
        (state: IChannel) => state.channelCurrencySymbol.value
    );

    const budgetInputRef = useRef(null);

    const mediaPlanBudget = useSelector(
        (state: { mediaPlanBudget: { value: number } }) =>
            state.mediaPlanBudget.value
    );

    const [budgetPopoverVisible, setBudgetPopoverVisible] = useState(false);

    const handleBackClickConfirm = () => {
        resetMediaPlanningConfig()
    };

    const handleBudgetChangeSubmit = () => {
        const finalValue = budgetInputRef.current.input.value;
        dispatch(setMediaPlanBudget(finalValue));
        setBudgetPopoverVisible(false);
        const adjustedPlanData = adjustOverallMediaPlanData(
            mediaPlanData,
            finalValue
        );
        dispatch(setGenerateMediaPlanData(adjustedPlanData));
    };

    const mediaPlanData = useSelector(
        (state: { generateMediaPlanData: { value: TMediaPlanRowData } }) =>
            state.generateMediaPlanData.value
    );

    const handleBudgetChangeCancel = () => {
        setBudgetPopoverVisible(false);
    };

    const handleSaveMediaPlanClick = async () => {
        setSavePlanLoading(true);
        try {
            const planConfig = {
                mediaPlanBudget: Number(mediaPlanBudget),
                planMonth: Number(planMonth),
                planName,
                ...(planId && { planId }),
                ...(planVersion && { planVersion }),
            };

            if (isMediaPlanEdit) {
                const originalPlanDataMap = getMappedPlanData(
                    originalMediaPlanData
                );
                const editedPlanDataMap = getMappedPlanData(mediaPlanData);

                const { planData, modifiedMetrices: changeLogs } =
                    compareMediaPlanData(
                        originalPlanDataMap,
                        editedPlanDataMap
                    );

                const invalidPlanItems = validatePlanData(planData);
                if (invalidPlanItems.length)
                    throw new Error(`Some cells contain invalid values. Please review the cells marked in red!!`);

                const response = await editMediaPlan({
                    planConfig,
                    planData,
                    changeLogs,
                });

                if (!response.status) {
                    throw new Error(response.message);
                }

                showToast("success", response.message);
            } else {
                const planData = Object.keys(mediaPlanGridApiMap).reduce(
                    (acc: TMediaPlanRowData, level: string) => {
                        const planLevelData: IMediaPlanRowObj[] = [];
                        mediaPlanGridApiMap[level].forEachNode((node) => {
                            const recordKey =
                                GENERATE_MEDIA_PLAN_CONFIG[level]
                                    ?.fieldColumn ||
                                GENERATE_MEDIA_PLAN_CONFIG["Default"]
                                    .fieldColumn;

                            const rowData = node.data;

                            planLevelData.push({
                                recordKey: rowData[recordKey],
                                planDataMetrices: pick(
                                    rowData,
                                    MEDIA_PLAN_METRICES
                                ) as any,
                            });
                        });

                        acc[level] = planLevelData;
                        return acc;
                    },
                    {}
                );

                const invalidPlanItems = validatePlanData(planData);
                if (invalidPlanItems.length)
                    throw new Error(`Some cells contain invalid values. Please review the cells marked in red!!`);

                const response = await createMediaPlan({
                    planConfig,
                    planData,
                });

                if (!response.status) {
                    throw new Error(response.message);
                }

                setSavePlanLoading(false);
                showToast("success", response.message);
            }

            // list latest plan data post making modifiedMetrices
            await listMediaPlans();

            // reset the state values
            handleBackClickConfirm();
            setSavePlanLoading(false);
        } catch (err: any) {
            showToast("danger", err.message);
            console.error(`${err.message}`);
            setSavePlanLoading(false);
        }
    };

    const showConfirm = () => {
        Modal.confirm({
            title: "Confirmation",
            content:
                "Any modifiedMetrices made will be lost. Are you sure you want to go back?",
            className: "plan-confirm-modal",
            okText: "Yes",
            cancelText: "Cancel",
            onOk: handleBackClickConfirm,
        });
    };

    const handleChangeLogClick = () => {
        dispatch(setShowMediaPlanChangeLogs(true));
    };

    useEffect(() => {
        if (isMediaPlanEdit && mediaPlanData) {
            setOriginalMediaPlanData(mediaPlanData);
        }
        //only want to run this once to save the inital plan data when a plan is edited.
    }, [isMediaPlanEdit]);

    const hasMediaPlanChanged = () => {
        if (!isMediaPlanEdit) return true;
        if (!originalMediaPlanData || !mediaPlanData) return false;
        return !isEqual(originalMediaPlanData, mediaPlanData);
    };

    const budgetChangePopoverContent = (
        <div className="w-300 p-2">
            <div className="d-flex align-items-center gap-2">
                <Input
                    prefix={currencySymbol}
                    ref={budgetInputRef}
                    type="number"
                    style={{ flex: 2, minWidth: "150px" }}
                    defaultValue={mediaPlanBudget}
                />
                <Button
                    size="small"
                    onClick={handleBudgetChangeSubmit}
                    type="primary"
                >
                    Save
                </Button>
                <Button
                    size="small"
                    onClick={handleBudgetChangeCancel}
                    style={{ color: "#ff4d4f", border: "none" }}
                >
                    Cancel
                </Button>
            </div>
        </div>
    );

    const handleExportMediaPlanClick = () => {
        const sheetsData: string[] = [];

        Object.entries(mediaPlanGridApiMap).forEach(([sheetName, gridApi]) => {
            gridApi.forEachNode((node) => {
                node.setSelected(true);
            });

            const sheetData = gridApi.getSheetDataForExcel({
                processCellCallback: (params) => {
                    if (params.column.getColId().includes("perc")) {
                        return `${params.value}%`;
                    }
                    return params.value;
                },
                onlySelected: true,
                sheetName: sheetName,
                rowHeight: 40
            });

            if (sheetData) {
                sheetsData.push(sheetData);
            }

            gridApi.deselectAll();
        });

        const clientName = (
            document.getElementsByClassName("client-info") as HTMLSelectElement
        )[0].innerText
            .split(" ")
            .join("-")
            .replace(/[().]/g, "");

        const tempGridApiRef = Object.values(mediaPlanGridApiMap)[0];
        tempGridApiRef.exportMultipleSheetsAsExcel({
            data: sheetsData,
            fileName: `${clientName}-${MONTH_OPTIONS[+planMonth - 1].label
                }-Media-Plan.xlsx`,
        });
    };

    return (
        <Layout>
            <div className="bg-white ps-3 pt-3 pb-3 shadow-sm rounded d-flex align-items-center justify-content-between">
                <span className="fs-6 fw-bold">{`${planName}`}</span>
                <Button
                    className="me-3 d-flex align-items-center p-0 border-0 bg-transparent"
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={
                        isMediaPlanEdit || isMediaPlanCreate
                            ? showConfirm
                            : handleBackClickConfirm
                    }
                >
                    Back to Listing
                </Button>
            </div>
            <Divider style={{ margin: "0px" }} />
            <div className="bg-white w-100 h-45 ps-3 pt-2 pb-2 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <span className="me-2 fs-6">
                        Planning for{" "}
                        <b>{`${MONTH_OPTIONS[+planMonth - 1].label || "1"}`}</b>
                        , Budget:{" "}
                        <b>{`${internationalizeNumericValue(
                            mediaPlanBudget
                        )}`}</b>
                    </span>
                    <Popover
                        content={budgetChangePopoverContent}
                        placement={"left"}
                        trigger="click"
                        open={budgetPopoverVisible}
                        onOpenChange={setBudgetPopoverVisible}
                    >
                        <div
                            className="text-danger fw-bold"
                            style={{
                                cursor: "pointer",
                                display:
                                    isMediaPlanEdit || isMediaPlanCreate
                                        ? "unset"
                                        : "none",
                            }}
                            onClick={(e) => {
                                // Prevent the popover from toggling when clicked outside
                                e.stopPropagation();
                                setBudgetPopoverVisible(!budgetPopoverVisible);
                            }}
                        >
                            <BsPencilFill className="me-1 mb-1" />
                            Modify
                        </div>
                    </Popover>
                </div>
                <div className="me-3 d-flex align-items-center">
                    {/* <div className="me-2">
                        <Select
                            mode="multiple"
                            style={{ width: "200px" }}
                            placeholder="Add more tag types"
                            optionLabelProp="label"
                        >
                            <Option value="option1" label="Option 1">
                                Option 1
                            </Option>
                            <Option value="option2" label="Option 2">
                                Option 2
                            </Option>
                            <Option value="option3" label="Option 3">
                                Option 3
                            </Option>
                        </Select>
                    </div> */}
                    <Button
                        className="me-1"
                        type="link"
                        style={{
                            display: isMediaPlanEdit ? "unset" : "none",
                        }}
                        onClick={handleChangeLogClick}
                    >
                        Change Log
                    </Button>
                    <Button
                        type="primary"
                        className="me-3"
                        style={{
                            display:
                                isMediaPlanEdit || isMediaPlanCreate
                                    ? "unset"
                                    : "none",
                        }}
                        onClick={handleSaveMediaPlanClick}
                        loading={savePlanLoading}
                        disabled={isMediaPlanEdit && !hasMediaPlanChanged()}
                    >
                        Save
                    </Button>
                    <Button
                        style={{ backgroundColor: "black", color: "white" }}
                        onClick={handleExportMediaPlanClick}
                    >
                        <FaDownload />{" "}
                        <span style={{ marginLeft: "5px" }}>Export</span>
                    </Button>
                </div>
            </div>
            <div
                className="main-content-body py-3"
                style={{
                    overflowY: "auto",
                    height: "80vh",
                }}
            >
                {children}
            </div>
            {showMediaPlanChangeLogs && (
                <>
                    <MediaPlanChangeLogs planId={planId} />
                </>
            )}
        </Layout>
    );
};

export default MediaPlanLayout;
